import { NumberPatternType } from '@dreryk/react-common';
import { Select, StyledFlex } from '@dreryk/react-components';

import { DosageTimeUnit } from '~libs.shared.prescription';

import { ExtendedDosageFormController } from '../ExtendedDosageForm.types';
import { DOSAGE_DURATION_UNITS } from '../../extendedDosage.utils';

import { useDosagePeriodTimeUnitLabels, useStageFieldValidationRules } from './DosagePeriodField.hooks';
import { StyledDosagePeriodAmountInput, StyledDosagePeriodUnitSelect } from './DosagePeriodField.styled';
import { DosagePeriodAmountSteps } from './DosagePeriodField.types';
import { registerStageField, StageFieldName } from './StageFieldList.utils';

type DosagePeriodFieldProps = Pick<ExtendedDosageFormController, 'register' | 'watch' | 'trigger'> & {
   readonly amountName: StageFieldName;
   readonly unitName: StageFieldName;
   readonly amountDefaultValue: number;
   readonly unitDefaultValue: DosageTimeUnit;
   readonly testId: string;
   readonly invalid: boolean;
   readonly units?: DosageTimeUnit[];
   readonly unitDisabled?: boolean;
   readonly amountSteps?: DosagePeriodAmountSteps;
   readonly onUnitSelect?: (unit: DosageTimeUnit | null) => void;
};

export const DosagePeriodField = (props: DosagePeriodFieldProps): JSX.Element => {
   const { timeUnitLabel, timeUnitLabelExtractor } = useDosagePeriodTimeUnitLabels({
      watch: props.watch,
      amountName: props.amountName,
      amountDefaultValue: props.amountDefaultValue,
   });
   const amountValidationRules = useStageFieldValidationRules({
      trigger: props.trigger,
      name: props.amountName,
      invalid: props.invalid,
   });
   const unitValidationRules = useStageFieldValidationRules({
      trigger: props.trigger,
      name: props.unitName,
      invalid: props.invalid,
   });

   return (
      <StyledFlex gap={'4px'} alignItems={'flex-start'}>
         <StyledDosagePeriodAmountInput
            testId={`${props.testId}-amount`}
            controller={registerStageField<number | null>(props.register, props.amountName, amountValidationRules)}
            defaultValue={props.amountDefaultValue}
            min={1}
            pattern={NumberPatternType.POSITIVE_INTEGER}
            onIncrement={props.amountSteps?.increment}
            onDecrement={props.amountSteps?.decrement}
            autoComplete={false}
            compact
         />
         <StyledDosagePeriodUnitSelect<DosageTimeUnit>
            testId={`${props.testId}-unit`}
            triggerLabelExtractor={timeUnitLabelExtractor}
            controller={registerStageField<DosageTimeUnit>(props.register, props.unitName, unitValidationRules)}
            defaultValue={props.unitDefaultValue}
            disabled={props.unitDisabled}
            onSelect={props.onUnitSelect}
            compact
         >
            <Select.List<DosageTimeUnit> items={props.units ?? DOSAGE_DURATION_UNITS}>
               {(unit: DosageTimeUnit) => (
                  <Select.Item item={unit} testId={unit} key={unit} label={timeUnitLabel(unit)} />
               )}
            </Select.List>
         </StyledDosagePeriodUnitSelect>
      </StyledFlex>
   );
};
